import { FC, type JSX } from 'react';

import { spokenFieldHint } from '@eneco/components/spoken-field/spoken-field';
import { productUnits } from '@eneco/enumerations/productUnits';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Button, Form, InputText, Stack, Text } from '@eneco/sparky';
import { Fields } from '@sitecore/types/SolarPanelsRedeliveryStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';


import { SOLAR_PANEL_OUTPUT } from '../../helpers';

interface FormFields {
  solarPanelsQuantity: string;
}

interface Props {
  textualData?: Fields;
  handleAction: (output: number) => void;
  handleCancel: () => void;
}

export const SolarPanelsOutputCalculatorForm: FC<Props> = ({
  textualData,
  handleAction,
  handleCancel,
}): JSX.Element => {
  const formSchema = yup.object({
    solarPanelsQuantity: yup
      .number()
      .integer(textualData?.redeliveryEstimateModal?.solarPanelsQuantityFormField?.value?.validationMessage)
      .typeError(textualData?.redeliveryEstimateModal?.solarPanelsQuantityFormField?.value?.validationMessage ?? '')
      .required(textualData?.redeliveryEstimateModal?.solarPanelsQuantityFormField?.value?.requiredMessage)
      .min(1, textualData?.redeliveryEstimateModal?.solarPanelsQuantityFormField?.value?.validationMinValueMessage)
      .max(99, textualData?.redeliveryEstimateModal?.solarPanelsQuantityFormField?.value?.validationMaxValueMessage),
  });

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors, isValid },
  } = useForm<FormFields>({
    mode: 'onChange',
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const solarPanelsQuantity = watch('solarPanelsQuantity');

  const submit = () => {
    handleAction(Number(solarPanelsQuantity) * SOLAR_PANEL_OUTPUT);
  };

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <Stack direction="column" gap="10">
        <Stack.Item>
          <InputText
            {...register('solarPanelsQuantity')}
            label={textualData?.redeliveryEstimateModal?.solarPanelsQuantityFormField?.value?.label ?? ''}
            hint={spokenFieldHint(
              textualData?.redeliveryEstimateModal?.solarPanelsQuantityFormField?.value?.hint,
              textualData?.redeliveryEstimateModal?.solarPanelsQuantityFormField?.value?.placeholder,
            )}
            error={errors.solarPanelsQuantity?.message}
          />

          <Box paddingTop="6">
            <Stack direction="column">
              <Text>{textualData?.redeliveryEstimateModal?.estimateText?.value}</Text>
              <Text size="BodyXL" weight="bold">
                {isValid ? Number(solarPanelsQuantity) * SOLAR_PANEL_OUTPUT : 0} {productUnits.electricity}
              </Text>
            </Stack>
          </Box>
        </Stack.Item>
        <Stack direction={{ initial: 'column', md: 'row' }} gap="2">
          <Button type="submit">
            {textualData?.redeliveryEstimateModal?.solarPanelsQuantityDialog?.value?.submitButtonText}
          </Button>
          <Button action="secondary" onClick={handleCancel}>
            {textualData?.redeliveryEstimateModal?.solarPanelsQuantityDialog?.value?.cancelButtonText}
          </Button>
        </Stack>
      </Stack>
    </Form>
  );
};
